import { NOT_SAVED_ID } from "app/model"
import { ICoordinate } from "coordinate/model"
import {
  ADD_COORDINATE_IMAGE_ID,
  COORDINATE_CENTER_LAYER_ID,
  COORDINATE_CIRCLE_LAYER_ID,
  COORDINATE_IMAGE_ID,
  COORDINATE_SOURCE_ID,
  CURSOR_POINTER,
  DISTANCE_UNITS,
  MARKER_ICON_HEIGHT,
  TEMP_COORDINATE_CENTER_LAYER_ID,
  TEMP_COORDINATE_CIRCLE_LAYER_ID,
  TEMP_COORDINATE_SOURCE_ID,
  UPDATE_COORDINATE_IMAGE_ID
} from "form/map/model"
import { state } from "form/map/state"
import { IFormCoordinate } from "form/model"
import {
  getDistanceUnit,
  getGeoTargetsForm,
  isImperialDistances,
  isValidLatitude as isValidLatitudeFn,
  isValidLongitude as isValidLongitudeFn,
  isValidRadius as isValidRadiusFn
} from "form/tools/geo-targets"
import { Feature, Point, Polygon } from "geojson"
import { ILocation } from "geometry/model"
import { geometrySVC } from "geometry/service"
import { getFeature } from "geometry/tools/common"
import { GeoJSONSourceRaw, Map } from "mapbox-gl"
import { fitFeaturesOnMap, getMapMouseCursor, panTo, setMapMouseCursor, setSourceData } from "./common"

export function getCoordinatesPolygons(): Polygon[] {
  return state.featuresByType.coordinates.filter(f => f.geometry.type === "Polygon").map(f => f.geometry) as Polygon[]
}

export function getCoordinatesFeatures(coordinates: ICoordinate[]): Array<Feature<Point | Polygon, IFormCoordinate>> {
  const features = []
  for (const coordinate of coordinates) {
    features.push(...getCoordinateFeatures(coordinate))
  }
  return features
}

export function getCoordinateFeatures(coordinate: ICoordinate): Array<Feature<Point | Polygon, IFormCoordinate>> {
  const location: ILocation = {
    x: coordinate.longitude,
    y: coordinate.latitude
  }
  const newFeature = getFeature(location) as Feature<Point, IFormCoordinate>
  newFeature.properties = coordinate
  newFeature.id = state.nextFeatureId++
  const features: Array<Feature<Point | Polygon, IFormCoordinate>> = [newFeature]
  if (coordinate.radius !== 0) {
    const circleFeature: Feature<Polygon, IFormCoordinate> = geometrySVC.getCircleFeature(
      location,
      coordinate.radius,
      DISTANCE_UNITS.METERS
    ) as Feature<Polygon, IFormCoordinate>
    circleFeature.properties = coordinate
    features.push(circleFeature)
  }
  return features
}

export function displayNewCoordinateFeature(coordinate: ICoordinate): void {
  const toAddFeatures = getCoordinateFeatures(coordinate)
  state.featuresByType.coordinates = [...state.featuresByType.coordinates, ...toAddFeatures]
  if (state.geoTargets.map) {
    setSourceData(state.geoTargets.map, COORDINATE_SOURCE_ID, state.featuresByType.coordinates)
  }
  if (state.inventory.map) {
    setSourceData(state.inventory.map, COORDINATE_SOURCE_ID, state.featuresByType.coordinates)
  }
  setTempCoordinateFeature(null)
}

export function updateCoordinateFeature(coordinate: IFormCoordinate): void {
  // remove features
  state.featuresByType.coordinates = state.featuresByType.coordinates.filter(f => f.properties.id !== coordinate.id)
  // add again
  displayNewCoordinateFeature(coordinate)
  stopUpdatingGeoTargetsFormOnMap()
}

export function setCoordinateFeatures(coordinates: ICoordinate[]): void {
  state.featuresByType.coordinates = getCoordinatesFeatures(coordinates)
  if (state.geoTargets.map) {
    setSourceData(state.geoTargets.map, COORDINATE_SOURCE_ID, state.featuresByType.coordinates)
  }
  if (state.inventory.map) {
    setSourceData(state.inventory.map, COORDINATE_SOURCE_ID, state.featuresByType.coordinates)
  }
}

export function removeCoordinateFeatures(coordinateIds: number[]): void {
  state.featuresByType.coordinates = state.featuresByType.coordinates.filter(
    f => !coordinateIds.includes(f.properties.id)
  )
  if (state.geoTargets.map) {
    setSourceData(state.geoTargets.map, COORDINATE_SOURCE_ID, state.featuresByType.coordinates)
  }
  if (state.inventory.map) {
    setSourceData(state.inventory.map, COORDINATE_SOURCE_ID, state.featuresByType.coordinates)
  }
}

export function centerMapOnCoordinateFeature(coordinateId: number): void {
  const feature = state.featuresByType.coordinates.find(f => f.properties.id === coordinateId)
  if (!feature) {
    // !! mismatch between map and redux states should never happened !!
    console.warn(`Coordinate feature id="${coordinateId}" not found`)
    return
  }
  if (state.geoTargets.map) {
    panTo(state.geoTargets.map, {
      x: feature.properties.longitude,
      y: feature.properties.latitude
    })
  }
  if (state.inventory.map) {
    panTo(state.inventory.map, {
      x: feature.properties.longitude,
      y: feature.properties.latitude
    })
  }
}

export function refreshTempCoordinateMap(): void {
  if (state.geoTargets.tempCoordinateFeatures !== null) {
    const point = state.geoTargets.tempCoordinateFeatures[0]
    setTempCoordinateFeature(
      {
        x: point.geometry.coordinates[0] as number,
        y: point.geometry.coordinates[1] as number
      },
      true
    )
  }
}

export function setTempCoordinateFeature(location: ILocation | null, noPan?: boolean): void {
  if (location === null) {
    state.geoTargets.tempCoordinateFeatures = null
    if (state.geoTargets.map) {
      setSourceData(state.geoTargets.map, TEMP_COORDINATE_SOURCE_ID, [])
    }
    return
  }
  const geoTargetsForm = getGeoTargetsForm()
  const features: Array<Feature<Point | Polygon>> = [
    getFeature(location, { id: geoTargetsForm.toUpdateEntityId }) as Feature<Point>
  ]
  if (isValidRadiusFn(geoTargetsForm.radius)) {
    const toUnit = DISTANCE_UNITS.METERS
    const fromUnit = isImperialDistances() ? DISTANCE_UNITS.MILES : DISTANCE_UNITS.KILOMETERS
    const radiusInMeter: number = geometrySVC.convertLength(Number(geoTargetsForm.radius as any), toUnit, fromUnit)
    features.push(geometrySVC.getCircleFeature(location, radiusInMeter, DISTANCE_UNITS.METERS))
  }
  state.geoTargets.tempCoordinateFeatures = features
  if (state.geoTargets.map) {
    checkTempCoordinateLayersAreVisible(state.geoTargets.map)
    setSourceData(state.geoTargets.map, TEMP_COORDINATE_SOURCE_ID, features)
    if (!noPan) {
      panTo(state.geoTargets.map, location)
    }
  }
}

export function setTempCoordinateLayersVisibility(visibility: "none" | "visible"): void {
  const map = state.geoTargets.map
  if (!map) {
    return
  }
  const layerIds = getGeoTargetTempCoordinateLayerIds()
  for (const layerId of layerIds) {
    if (map.getLayer(layerId)) {
      if (map.getLayoutProperty(layerId, "visibility") !== visibility) {
        map.setLayoutProperty(layerId, "visibility", visibility)
      }
    }
  }
  if (visibility === "visible") {
    const { latitude, longitude, radius } = getGeoTargetsForm()
    const isValidLatitude = isValidLatitudeFn(latitude)
    const isValidLongitude = isValidLongitudeFn(longitude)
    if (isValidLatitude && isValidLongitude) {
      const validRadius = isValidRadiusFn(radius) ? Number(radius) : 0
      const location = { x: Number(longitude), y: Number(latitude) }
      let feature: Feature
      if (!validRadius) {
        feature = getFeature(location) as Feature
      } else {
        feature = geometrySVC.getCircleFeature(location, validRadius, getDistanceUnit()) as Feature<
          Polygon,
          IFormCoordinate
        >
      }
      fitFeaturesOnMap(map, [feature])
    }
  }
}

export function createCoordinatesLayers(map: Map, avoidClickListeners?: boolean): void {
  if (map.getSource(COORDINATE_SOURCE_ID)) {
    return
  }
  let cursorBeforeHover = getMapMouseCursor(map)
  map.addSource(COORDINATE_SOURCE_ID, {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: state.featuresByType.coordinates
    },
    maxzoom: 24
  } as GeoJSONSourceRaw)
  map.addLayer(
    {
      id: COORDINATE_CIRCLE_LAYER_ID,
      type: "fill",
      source: COORDINATE_SOURCE_ID,
      paint: {
        "fill-color": "#000000",
        "fill-opacity": 0.1
      },
      filter: getDefaultCoordinateTypeFilter(true)
    },
    map.getLayer(TEMP_COORDINATE_CIRCLE_LAYER_ID) ? TEMP_COORDINATE_CIRCLE_LAYER_ID : undefined
  )
  map.addLayer(
    {
      id: COORDINATE_CENTER_LAYER_ID,
      type: "symbol",
      source: COORDINATE_SOURCE_ID,
      layout: {
        "icon-image": COORDINATE_IMAGE_ID,
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "icon-offset": [0, -MARKER_ICON_HEIGHT / 2]
      },
      filter: getDefaultCoordinateTypeFilter(false)
    },
    map.getLayer(TEMP_COORDINATE_CIRCLE_LAYER_ID) ? TEMP_COORDINATE_CIRCLE_LAYER_ID : undefined
  )
  if (!avoidClickListeners) {
    map.on("mousemove", COORDINATE_CENTER_LAYER_ID, event => {
      const currentCursor = getMapMouseCursor(map)
      if (event.features && event.features.length > 0) {
        if (currentCursor !== CURSOR_POINTER) {
          cursorBeforeHover = currentCursor
          setMapMouseCursor(map, CURSOR_POINTER)
        }
      } else if (currentCursor === CURSOR_POINTER) {
        setMapMouseCursor(map, cursorBeforeHover)
      }
    })
    map.on("mouseleave", COORDINATE_CENTER_LAYER_ID, event => {
      const currentCursor = getMapMouseCursor(map)
      if (currentCursor === CURSOR_POINTER) {
        setMapMouseCursor(map, cursorBeforeHover)
      }
    })
  }
}

export function createTempCoordinateLayers(map: Map): void {
  if (map.getSource(TEMP_COORDINATE_SOURCE_ID)) {
    return
  }
  map.addSource(TEMP_COORDINATE_SOURCE_ID, {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: state.geoTargets.tempCoordinateFeatures ? state.geoTargets.tempCoordinateFeatures : []
    },
    maxzoom: 24
  } as GeoJSONSourceRaw)
  map.addLayer({
    id: TEMP_COORDINATE_CIRCLE_LAYER_ID,
    type: "fill",
    source: TEMP_COORDINATE_SOURCE_ID,
    paint: {
      "fill-color": "orange",
      "fill-opacity": 0.2
    },
    filter: getDefaultCoordinateTypeFilter(true)
  })
  map.addLayer({
    id: TEMP_COORDINATE_CENTER_LAYER_ID,
    type: "symbol",
    source: TEMP_COORDINATE_SOURCE_ID,
    layout: {
      "icon-image": ["case", ["==", ["get", "id"], NOT_SAVED_ID], ADD_COORDINATE_IMAGE_ID, UPDATE_COORDINATE_IMAGE_ID],
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
      "icon-offset": [0, -MARKER_ICON_HEIGHT / 2]
    },
    filter: getDefaultCoordinateTypeFilter(false)
  })
}

export function startUpdatingGeoTargetsFormOnMap(coordinate: IFormCoordinate): void {
  if (state.geoTargets.map) {
    setTempCoordinateFeature({
      x: coordinate.longitude,
      y: coordinate.latitude
    })
    state.geoTargets.map.setFilter(
      COORDINATE_CENTER_LAYER_ID,
      getGeoTargetCoordinateFilter(["!=", ["get", "id"], coordinate.id], false)
    )
    state.geoTargets.map.setFilter(
      COORDINATE_CIRCLE_LAYER_ID,
      getGeoTargetCoordinateFilter(["!=", ["get", "id"], coordinate.id], true)
    )
  }
}

export function stopUpdatingGeoTargetsFormOnMap(): void {
  if (state.geoTargets.map) {
    state.geoTargets.map.setFilter(COORDINATE_CENTER_LAYER_ID, getGeoTargetCoordinateFilter(undefined, false))
    state.geoTargets.map.setFilter(COORDINATE_CIRCLE_LAYER_ID, getGeoTargetCoordinateFilter(undefined, true))
    setTempCoordinateFeature(null)
  }
}

export function getDefaultCoordinateTypeFilter(onlyPolygon: boolean): any[] {
  return ["==", ["geometry-type"], onlyPolygon ? "Polygon" : "Point"]
}

export function getGeoTargetCoordinateFilter(newFilter: any[] | undefined, onlyPolygon: boolean) {
  const newFilters = ["all", getDefaultCoordinateTypeFilter(onlyPolygon)]
  if (newFilter) {
    newFilters.push(newFilter)
  }
  return newFilters
}

export function getGeoTargetCoordinateLayerIds(): string[] {
  return [COORDINATE_CENTER_LAYER_ID, COORDINATE_CIRCLE_LAYER_ID]
}

function getGeoTargetTempCoordinateLayerIds(): string[] {
  return [TEMP_COORDINATE_CENTER_LAYER_ID, TEMP_COORDINATE_CIRCLE_LAYER_ID]
}

function checkTempCoordinateLayersAreVisible(map: Map): void {
  const layerIds = getGeoTargetTempCoordinateLayerIds()
  for (const layerId of layerIds) {
    if (map.getLayer(layerId)) {
      if (map.getLayoutProperty(layerId, "visibility") !== "visible") {
        map.setLayoutProperty(layerId, "visibility", "visible")
      }
    }
  }
}
